import { useRef, useState } from "react";
import { Loader2, Paperclip, UploadCloud } from "lucide-react";
import { ALLOWED_ATTACHMENT_MIME_TYPES, type FileKind } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { useFileUpload } from "./use-file-upload";

type Props = {
  kind: FileKind;
  onUploaded: (file: { fileId: string; name: string }) => void;
  disabled?: boolean;
};

/** Zona de arrastar-e-soltar com botão embutido — usada em contextos pontuais (anexar arquivo a
 *  uma entidade específica, ex: aba Arquivos de um pedido de prótese). Na tela principal de
 *  Arquivos o upload já é via FilesUploadButton (botão compacto) + arraste direto na pasta. */
export function FilesDropzone({ kind, onUploaded, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const { uploading, total, doneCount, handleFiles } = useFileUpload({ kind, onUploaded });

  async function handleFilesAndReset(fileList: FileList | null) {
    await handleFiles(fileList);
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (disabled || uploading) return;
    void handleFilesAndReset(e.dataTransfer.files);
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled && !uploading) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={cn(
        "flex min-h-[140px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors",
        dragOver
          ? "border-brand-400 bg-brand-50 dark:border-primary/60 dark:bg-primary/10"
          : "border-zinc-200 dark:border-border",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        // Extensões além do MIME: STL/PLY de scanner intraoral quase sempre chegam com
        // `file.type` vazio ou "application/octet-stream" — o navegador filtra pelo picker por
        // extensão nesse caso, o MIME da allowlist sozinho não cobre.
        accept={
          kind === "PROSTHESIS_ATTACHMENT" ? `${ALLOWED_ATTACHMENT_MIME_TYPES.join(",")},.stl,.ply,.zip` : undefined
        }
        className="hidden"
        onChange={(e) => void handleFilesAndReset(e.target.files)}
      />
      <UploadCloud className="h-6 w-6 text-zinc-300 dark:text-muted-foreground/60" />
      <p className="text-xs text-zinc-400 dark:text-muted-foreground">
        {dragOver ? "Solte os arquivos aqui" : "Arraste arquivos aqui"}
      </p>
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
        {uploading ? `Enviando... (${doneCount}/${total})` : "Anexar arquivos"}
      </button>
    </div>
  );
}
